import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Plus, FileDown, Copy, ArrowRight, Trash2 } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { formatCurrency, formatDate } from "../lib/format";
import { toast } from "sonner";
import { API } from "../lib/api";
import OCRUploadDialog from "../components/OCRUploadDialog";

export default function Quotes() {
  const { t, lang } = useI18n();
  const [quotes, setQuotes] = useState([]);
  const [clientsMap, setClientsMap] = useState({});

  const load = async () => {
    const [q, c] = await Promise.all([api.get("/quotes"), api.get("/clients")]);
    setQuotes(q.data);
    const m = {};
    c.data.forEach((x) => (m[x.id] = x));
    setClientsMap(m);
  };

  useEffect(() => {
    load();
  }, []);

  const duplicate = async (id) => {
    await api.post(`/quotes/${id}/duplicate`);
    toast.success("Duplicat");
    load();
  };

  const removeQuote = async (id, number) => {
    if (!window.confirm(
      lang === "es"
        ? `¿Eliminar el presupuesto ${number}? Esta acción no se puede deshacer.`
        : `Eliminar el pressupost ${number}? Aquesta acció no es pot desfer.`
    )) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success(lang === "es" ? "Presupuesto eliminado" : "Pressupost eliminat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "es" ? "Error al eliminar" : "Error en eliminar"));
    }
  };

  const convert = async (id) => {
    await api.post(`/quotes/${id}/convert`);
    toast.success("Convertit a factura!");
    load();
  };

  const downloadPdf = (id, number) => {
    const token = localStorage.getItem("hosys_token");
    const url = `${API}/quotes/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">{t("quotes.title")}</h1>
          <p className="text-sm text-slate-400 mt-1">{quotes.length} documents</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <OCRUploadDialog kind="quote" />
          <Link to="/app/quotes/new">
            <Button
              data-testid="new-quote-btn"
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400"
            >
              <Plus size={16} className="mr-2" /> {t("quotes.new")}
            </Button>
          </Link>
        </div>
      </div>

      {quotes.length === 0 ? (
        <div className="card-surface p-12 text-center text-slate-500">{t("quotes.empty")}</div>
      ) : (
        <div className="card-surface overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="quotes-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("quotes.number")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("quotes.client")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("quotes.date")}</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("quotes.total")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("quotes.status")}</th>
                {/* Sticky actions column — keeps the buttons visible when the
                    table needs horizontal scroll on landscape phones. */}
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium sticky right-0 bg-[#0B0E14]/95 backdrop-blur-sm shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.5)]">{t("quotes.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] group">
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/app/quotes/${q.id}`} className="text-blue-300 hover:text-blue-200" data-testid={`quote-link-${q.id}`}>
                      {q.number}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-slate-300">{clientsMap[q.client_id]?.name || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{formatDate(q.date, lang)}</td>
                  <td className="px-5 py-3 text-right font-medium">{formatCurrency(q.total, lang)}</td>
                  <td className="px-5 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-5 py-3 sticky right-0 bg-[#0B0E14]/95 group-hover:bg-[#10131A]/95 backdrop-blur-sm shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.5)]">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => downloadPdf(q.id, q.number)}
                        className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-blue-400"
                        title="PDF"
                        data-testid={`quote-pdf-${q.id}`}
                      >
                        <FileDown size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => duplicate(q.id)}
                        className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-blue-400"
                        title={t("quotes.duplicate")}
                      >
                        <Copy size={14} />
                      </button>
                      {q.status !== "converted" && (
                        <button
                          type="button"
                          onClick={() => convert(q.id)}
                          className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-emerald-400"
                          title={t("quotes.convert")}
                          data-testid={`quote-convert-${q.id}`}
                        >
                          <ArrowRight size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeQuote(q.id, q.number)}
                        className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"
                        title={lang === "es" ? "Eliminar" : "Eliminar"}
                        data-testid={`quote-delete-${q.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
